import { z } from 'zod';

import { CssVariablesSchema } from './SharedOptions';

export const OptionsSchema = z.object({
  isDefault: z.boolean().optional(),
  fonts: z
    .object({
      primaryFontUrl: z.string(),
      boldFontUrl: z.string(),
      lightFontUrl: z.string().optional(),
    })
    .optional(),
  images: z.object({
    heroBanner: z.string().optional(),
    localizedHeroBanner: z.record(z.string()).optional(),
    sessionTimeoutIcon: z.string().optional(),
    macroArticleIcon: z.string().optional(),
    attachmentIconPath: z.string().optional(),
    typingIndicatorAvatar: z
      .object({
        url: z.string(),
        alt: z.string().optional(),
        lottieUrl: z.string(),
      })
      .optional(),
    salesDisclosureHero: z.string().optional(),
    voipSidePanelIcon: z
      .object({
        url: z.string(),
        alt: z.string().optional(),
        lottieUrl: z.string().optional(),
      })
      .optional(),
  }),
  colors: z
    .object({
      primary: z.string().optional(),
      secondary: z.string().optional(),
      background: z
        .object({
          main: z.string(),
          light: z.string(),
          accent: z.string(),
          disabled: z.string().optional(),
          element: z.string().optional(),
          tonal: z.string().optional(),
        })
        .optional(),
      hoverBackground: z
        .object({
          primaryHover: z.string(),
        })
        .optional(),
      messageBackgrounds: z
        .object({
          outbound: z.string(),
          inbound: z.string(),
        })
        .optional(),
      messageText: z
        .object({
          inbound: z.string().optional(),
          outbound: z.string().optional(),
        })
        .optional(),
      text: z
        .object({
          primaryLight: z.string(),
          secondaryLight: z.string(),
          primaryDark: z.string(),
          secondaryDark: z.string(),
          disabled: z.string(),
          buttonRegular: z.string(),
          buttonHover: z.string(),
          error: z.string(),
          tonal: z.string().optional(),
        })
        .optional(),
      border: z
        .object({
          default: z.string(),
          dividerOnDark: z.string(),
          active: z.string(),
          error: z.string(),
        })
        .optional(),
      controls: z
        .object({
          uncheckedControl: z.string(),
          inboundChecked: z.string(),
          outboundChecked: z.string(),
          disabledControl: z.string(),
        })
        .optional(),
      scrollToBottom: z
        .object({
          background: z.string().optional(),
          hoverBackground: z.string().optional(),
        })
        .optional(),
      link: z
        .object({
          inboundDefaultLink: z.string(),
          inboundDisabledLink: z.string(),
          inboundHoverLink: z.string(),
          outboundDefaultLink: z.string(),
          outboundDisabledLink: z.string(),
          outboundHoverLink: z.string(),
        })
        .optional(),
      ratingIcon: z
        .object({
          INBOUND: z.string(),
          OUTBOUND: z.string(),
        })
        .optional(),
      voipSoundWave: z
        .object({
          gradientBottomColor: z.string().optional(),
          gradientTopColor: z.string().optional(),
        })
        .optional(),
    })
    .optional(),
  text: z.object({
    partnerFriendlyName: z.string().optional(),
    routeToAgentMessage: z.string().optional(),
    poweredByEntity: z.string().optional(),
    headerTitle: z.string().optional(),
    voipFallbackAgentName: z.string().optional(),
    voipExpertSubtitle: z.string().optional(),
    voipConnectingSubtitle: z.string().optional(),
  }),
  mainWidget: z
    .object({
      buttonVariant: z.string().optional(),
      buttonSize: z.string().optional(),
    })
    .optional(),
  privacyPopup: z
    .object({
      img: z.string(),
      title: z.string(),
      text: z.string(),
      url: z.string(),
    })
    .optional(),
  analytics: z
    .object({
      partnerId: z.string().optional(),
    })
    .optional(),
  experimental: z
    .object({
      imageFit: z.string().optional(),
      hideMessageStamps: z.boolean().optional(),
      showAttachmentButton: z.boolean().optional(),
      enableLongMessageSplitting: z.boolean().optional(),
      enableTroubleshootingImages: z.boolean().optional(),
      surveyFlowId: z.string().optional(),
      urlsForSidePanel: z.array(z.string()).optional(),
      hideSelectSubscriptionTitleAndPrice: z.boolean().optional(),
      enableGuidedMessageSkeletonLoader: z.boolean().optional(),
    })
    .optional(),
  disableOverscrollContainment: z.boolean().optional(),
  cssVariables: CssVariablesSchema.optional().catch(undefined),
});

export type Options = z.infer<typeof OptionsSchema>;
